import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import {
  CreateGroupRequest,
  ListGroupsQuery,
  UpdateGroupRequest,
} from './Groups.types';

import {
  listGroups,
  deleteGroup,
  createGroup,
  updateGroup,
  getGroup,
  groupActivateOrInactivate,
  listGroupsReduced,
} from './Groups.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { useIntl } from 'react-intl';
import { message, notification } from 'antd';
import notificationError from '@/utils/functions/notificationError';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';

export function useGroups(params?: ListGroupsQuery, enabled = true) {
  const query = useInfiniteQuery(
    ['groups', { ...params }],
    ({ pageParam }) => {
      const req: ListGroupsQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listGroups(req);
    },
    {
      enabled,
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useGroupsReduced(params?: ListGroupsQuery, enabled = true) {
  const query = useInfiniteQuery(
    ['groups-search-group', { ...params }],
    ({ pageParam }) => {
      const req: ListGroupsQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listGroupsReduced(req);
    },
    {
      enabled,
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useGroup(id: number) {
  const query = useQuery(['groups', id], () => getGroup(id), {
    enabled: hasPermissions([Authorities.REGISTER_GROUP_EDIT]),
  });

  useServiceError(query.error);

  return query;
}

export function useCreateGroup() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation((obj: CreateGroupRequest) => createGroup(obj), {
    async onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'groups.create.notification.success',
        }),
        description: intl.formatMessage({
          id: 'groups.create.notification.successDescription',
        }),
      });

      await queryClient.invalidateQueries(['groups']);
      await queryClient.invalidateQueries(['groups-search-group']);
    },

    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('Group already exists')
              ? 'O grupo já existe.'
              : err.includes('Already a Group with the name')
              ? 'Já existe um grupo cadastrado com essa descrição.'
              : err,
          ),
      });
    },
  });

  return mutation;
}

export function useUpdateGroup() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation((obj: UpdateGroupRequest) => updateGroup(obj), {
    async onSuccess(_, data) {
      notification.success({
        message: intl.formatMessage({
          id: 'groups.update.notification.success',
        }),
        description: intl.formatMessage({
          id: 'groups.update.notification.successDescription',
        }),
      });

      await Promise.all([
        queryClient.invalidateQueries(['groups', data.id]),
        queryClient.invalidateQueries(['groups']),
        queryClient.invalidateQueries(['groups-search-group']),
      ]);
    },

    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('Group already exists')
              ? 'O grupo já existe.'
              : err.includes('Already a Group with the name')
              ? 'Já existe um grupo cadastrado com essa descrição.'
              : err,
          ),
      });
    },
  });

  return mutation;
}

export function useDeleteGroup() {
  const intl = useIntl();
  const mutation = useMutation((id: number) => deleteGroup(id), {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'groups.remove.notification.success',
        }),
        description: intl.formatMessage({
          id: 'groups.remove.notification.successDescription',
        }),
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useActiveOrInactivateGroup() {
  const intl = useIntl();

  const mutation = useMutation(
    (groupId: number) => groupActivateOrInactivate(groupId),

    {
      onSuccess() {
        message.success(
          intl.formatMessage({ id: 'groups.status.message.success' }),
        );
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}
